import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
  MenuItem,
  FormControl,
  Select,
  InputLabel,
  Badge,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import EditIcon from "@mui/icons-material/Edit";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import { changeOrderStatus } from "../../../../../services/OrderStatus/OrderStatusApi";
import { ThemeContext } from "../../../../../context/ThemeContextProvider";
import { fetchDeliveryOrders } from "../../../../../services/pos/Tables";
import CreateOrderForm from "../../MakeOrder/delivery/OrderDetails/Openorder/CreateOrderForm";
import DeliveryModalContent from "../../MakeOrder/delivery/DeliveryModalContent";
import { usePermissions } from "../../../../../context/PermissionsAndFeatures/UserPermissionsContext";
import OrderItemsDialog from "./OrderItemsDialog";


const DeliverySection = () => {
  const { mode } = useContext(ThemeContext);
  const createFormRef = useRef(null);
  const searchInputRef = useRef(null);
  const [orders, setOrders] = useState([]);
  const [filterStatus, setFilterStatus] = useState("الكل");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [isAddItemsOpen, setIsAddItemsOpen] = useState(false);
  const [editOrderId, setEditOrderId] = useState(null);
  const [editStatusOpen, setEditStatusOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [pilotFilter, setPilotFilter] = useState("");
  const { hasPermission } = usePermissions();

  // هل يمكن للمستخدم رؤية طلبات الحالة "طلب خارجي"؟
  const canViewExternalOrders = hasPermission("orders_delivery_api_orders");

  // الحالات المتاحة للتغيير
  const statusOptions = [
    { value: "out_of_preparation", label: "مكتمل" },
    { value: "pending", label: "تجهيز" },
  ];

  // تغيير حالة الأوردر
  const handleChangeOrderStatus = async () => {
    if (!editOrderId || !selectedStatus) return;
    try {
      await changeOrderStatus(editOrderId, selectedStatus);
      setEditStatusOpen(false);
      setEditOrderId(null);
      setSelectedStatus("");
      loadOrders();
    } catch (err) {
      alert("فشل تغيير حالة الأوردر");
    }
  };

  const loadOrders = useCallback(async () => {
    try {
      const branchId = localStorage.getItem("branch_id");
      if (!branchId) throw new Error("❌ لا يوجد branch_id في localStorage");

      const data = await fetchDeliveryOrders(branchId);

      const enriched = data.map((item) => ({
        id: item.id,
        status:
          item.status === "pending"
            ? "في التجهيز"
            : item.status === "completed"
            ? "مكتمل"
            : item.status === "out_of_preparation"
            ? "مكتمل"
            : item.status === "underway"
            ? "قيد التوصيل"
            : item.status === "delivered"
            ? "تم التسليم"
            : item.status === "under_review"
            ? "طلب خارجي"
            : item.status,
        customer: item.customer?.name || "-",
        pilotName: item.delivery?.name || "-",
        phone: item.customer?.phone1 || "",
        address: item.customer?.address1 || "",
        time: item.open_time || "-",
        total: item.total || 0,
        orderNumber: `#0${item.general_increment}`,
        timer: 0,
        itemsCount: Array.isArray(item.items) ? item.items.length : 0,
      }));

      const finalList = canViewExternalOrders
        ? enriched
        : enriched.filter((o) => o.status !== "طلب خارجي");

      setOrders(finalList);
    } catch (err) {
      console.error("❌ فشل تحميل طلبات الدليفري:", err.message);
    }
  }, [canViewExternalOrders]);


  // ✅ تحميل الطلبات أول مرة + عند تغيير صلاحية عرض الطلبات الخارجية
useEffect(() => {
  loadOrders();
}, [loadOrders]);

// ✅ Listener لتحديث القائمة لما يحصل تعديل في أي مكان
useEffect(() => {
  const handleOrderUpdate = (e) => {
    const updatedOrderId = e?.detail?.orderId; // ممكن تستخدمه لو محتاج
    loadOrders(); // أهم حاجة: نحدث القائمة كلها
  };

  window.addEventListener("orderUpdated", handleOrderUpdate);
  window.addEventListener("refreshDeliveryOrders", handleOrderUpdate);

  return () => {
    window.removeEventListener("orderUpdated", handleOrderUpdate);
    window.removeEventListener("refreshDeliveryOrders", handleOrderUpdate);
  };
}, [loadOrders]);




  useEffect(() => {
    localStorage.setItem("current_component", "delivery");
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  const formatTime12 = (timeStr) => {
    if (!timeStr) return "-";
    const [h, m] = timeStr.split(":");
    let hour = Number(h);
    const ampm = hour >= 12 ? "م" : "ص";
    hour = hour % 12 || 12;
    return `${hour}:${m} ${ampm}`;
  };

  // حساب عدد الطلبات لكل حالة
  const tabStatuses = [
    { key: "الكل", label: "الكل" },
    { key: "مكتمل", label: "مكتمل" },
    { key: "في التجهيز", label: "في التجهيز" },
    { key: "قيد التوصيل", label: "قيد التوصيل" },
    { key: "تم التسليم", label: "تم التسليم" },
    { key: "طلب خارجي", label: "طلب خارجي" }, // 👈 تابة جديدة
  ];

  // لو المستخدم لا يملك الصلاحية نخفي تاب "طلب خارجي"
  const visibleTabStatuses = tabStatuses.filter(
    (t) => !(t.key === "طلب خارجي" && !canViewExternalOrders)
  );

  const getTabCount = (status) => {
    if (status === "الكل") {
      // عدد الطلبات المعروضة فعلياً في تاب الكل
      return orders.filter((order) => {
        let statusAr = order.status;
        if (order.status === "underway") statusAr = "قيد التوصيل";
        if (order.status === "delivered") statusAr = "تم التسليم";
        if (order.status === "under_review") statusAr = "طلب خارجي";
        // استبعاد "طلب خارجي" و "finished"
        return !(statusAr === "طلب خارجي" || order.status === "finished");
      }).length;
    }
    return orders.filter((order) => {
      let statusAr = order.status;
      if (order.status === "underway") statusAr = "قيد التوصيل";
      if (order.status === "delivered") statusAr = "تم التسليم";
      if (order.status === "under_review") statusAr = "طلب خارجي";
      return statusAr === status;
    }).length;
  };

  const filteredOrders = orders.filter((order) => {
    // تحويل حالة النظام إلى العربية للفلترة
    let statusAr = order.status;
    if (order.status === "underway") statusAr = "قيد التوصيل";
    if (order.status === "delivered") statusAr = "تم التسليم";
    if (order.status === "under_review") statusAr = "طلب خارجي";

    // في تاب "الكل" لا تعرض "طلب خارجي" ولا "finished"
    if (
      filterStatus === "الكل" &&
      (statusAr === "طلب خارجي" || order.status === "finished")
    )
      return false;

    const searchFields = [
      order.customer,
      order.pilotName,
      order.phone,
      order.address,
      order.time,
      order.total.toString(),
      order.orderNumber,
    ]
      .join(" ")
      .toLowerCase();

    return (
      (filterStatus === "الكل" || statusAr === filterStatus) &&
      searchFields.includes(searchQuery.toLowerCase()) &&
      (pilotFilter === "" || order.pilotName === pilotFilter)
    );
  });

  const statusColors = {
    مكتمل: "#4CAF50",
    "في التجهيز": "#FFC107",
    "قيد التوصيل": "#2196F3",
  };

  // NEW: حالة Dialog عرض أصناف الأوردر
  const [orderItemsDialogOpen, setOrderItemsDialogOpen] = useState(false);
  const [selectedOrderIdForItems, setSelectedOrderIdForItems] = useState(null);

  // NEW: دالة فتح Dialog عرض الأصناف
  const handleOpenOrderItems = (orderId) => {
    setSelectedOrderIdForItems(orderId);
    setOrderItemsDialogOpen(true);
  };

  // NEW: دالة إغلاق Dialog عرض الأصناف
  const handleCloseOrderItems = () => {
    setOrderItemsDialogOpen(false);
    setSelectedOrderIdForItems(null);
  };

  // اختصار F6 للتمرير إلى CreateOrderForm وتركيز أول حقل داخله
  useEffect(() => {
    const handleF6 = (e) => {
      if (e.key === "F6" || e.keyCode === 117) {
        e.preventDefault();
        if (!hasPermission("orders_other_edite_service")) return;
        if (createFormRef.current) {
          createFormRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
          // حاول تركيز أول عنصر قابل للتركيز داخل الفورم
          const first = createFormRef.current.querySelector("input, textarea, select, [tabindex]");
          if (first && typeof first.focus === "function") first.focus();
        }
      }
    };

    window.addEventListener("keydown", handleF6);
    return () => window.removeEventListener("keydown", handleF6);
  }, [hasPermission]);

  // ✅ اختصار F3 للتركيز على خانة البحث
  useEffect(() => {
    const handleF3 = (e) => {
      if (e.key === "F3" || e.keyCode === 114) {
        e.preventDefault();
        const input = searchInputRef.current;
        if (input) {
          if (typeof input.focus === "function") input.focus();
          if (typeof input.select === "function") input.select();
        }
      }
    };
    window.addEventListener("keydown", handleF3);
    return () => window.removeEventListener("keydown", handleF3);
  }, []);

  return (
    <Box sx={{ p: 2, bgcolor: mode === "dark" ? "#1F1D2B" : "#fff" }}>
      {/* CreateOrderForm ظاهر دائماً فوق الفلاتر والبحث */}
      {hasPermission("orders_other_edite_service") && (
        <Box sx={{ mb: 2 }} ref={createFormRef}>
          <CreateOrderForm
            onSuccess={(res) => {
              const createdId = res?.id;
              if (!createdId) return alert("❌ فشل في جلب رقم الطلب الجديد");

              setSelectedOrderId(createdId);
              // فتح واجهة إضافة الأصناف بعد الإنشاء
              setIsAddItemsOpen(true);
              loadOrders();
            }}
          />
        </Box>
      )}

      {/* فلاتر البحث */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", gap: 1 }}>
          {visibleTabStatuses.map((tab) => (
            <Button
              key={tab.key}
              variant={filterStatus === tab.key ? "contained" : "outlined"}
              onClick={() => setFilterStatus(tab.key)}
              size="small"
            >
              {tab.label}
              <Box
                component="span"
                sx={{
                  ml: 1,
                  px: 1.2,
                  py: 0.2,
                  bgcolor: "#fff",
                  color: "#232334",
                  borderRadius: "8px",
                  fontWeight: "bold",
                  fontSize: "13px",
                  minWidth: "28px",
                  display: "inline-block",
                }}
              >
                {getTabCount(tab.key)}
              </Box>
            </Button>
          ))}
        </Box>
        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField
            placeholder="البحث عن الطلبات..."
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            variant="outlined"
            sx={{ width: 250 }}
            inputRef={searchInputRef}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton onClick={() => setSearchQuery("")}>
                    {" "}
                    <ClearIcon />{" "}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {/* فلتر الدليفري */}
          <TextField
            select
            label="الدليفري"
            size="small"
            value={pilotFilter || ""}
            onChange={(e) => setPilotFilter(e.target.value)}
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="">الكل</MenuItem>
            {[...new Set(orders.map((o) => o.pilotName).filter(Boolean))].map(
              (name, idx) => (
                <MenuItem key={idx} value={name}>
                  {name}
                </MenuItem>
              )
            )}
          </TextField>
        </Box>
      </Box>

      {/* قائمة الطلبات */}
      <Grid container spacing={2}>
        {filteredOrders.map((order, idx) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={order.id}>
            <Paper
              sx={{
                background:
                  "linear-gradient(135deg, #232334 60%, #232334 100%)",
                color: "#fff",
                borderRadius: 4,
                boxShadow: "0 4px 24px #0004",
                p: 0,
                overflow: "hidden",
                minHeight: { xs: 180, sm: 185, md: 190 },
                position: "relative",
                cursor: "pointer",
                transition: "box-shadow 0.2s, transform 0.2s",
                "&:hover": {
                  boxShadow: "0 8px 32px #EA7C6988",
                  transform: "scale(1.03)",
                },
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
              onClick={() => {
                setSelectedOrderId(order.id);
                setIsAddItemsOpen(true);
              }}
            >
              {/* Header: Order Number & Status */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: { xs: 1, sm: 1.2 },
                  pb: { xs: 0.5, sm: 0.8 },
                  gap: 1,
                  position: "relative",
                  flexWrap: "wrap",
                }}
              >
                {/* NEW: أيقونة عدد الأصناف - في البداية */}
                {order.itemsCount > 0 && (
                  <Box
                    sx={{
                      zIndex: 10,
                      cursor: "pointer",
                      order: 1,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenOrderItems(order.id);
                    }}
                  >
                    <Badge
                      badgeContent={order.itemsCount}
                      color="error"
                      max={99}
                      sx={{
                        "& .MuiBadge-badge": {
                          fontSize: { xs: "8px", sm: "9px" },
                          fontWeight: 700,
                          minWidth: { xs: "14px", sm: "16px" },
                          height: { xs: "14px", sm: "16px" },
                          padding: "0 3px",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          bgcolor: mode === "dark" ? "#1a1a2e" : "#fff",
                          borderRadius: "50%",
                          width: { xs: 28, sm: 32 },
                          height: { xs: 28, sm: 32 },
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
                          border: `2px solid ${mode === "dark" ? "#444" : "#e0e0e0"}`,
                          transition: "all 0.2s",
                          "&:hover": {
                            transform: "scale(1.1)",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.35)",
                          },
                          "&:active": {
                            transform: "scale(0.95)",
                          },
                        }}
                      >
                        <RestaurantMenuIcon
                          sx={{
                            fontSize: { xs: 14, sm: 16 },
                            color: mode === "dark" ? "#fff" : "#666",
                          }}
                        />
                      </Box>
                    </Badge>
                  </Box>
                )}

                {/* الحالة - في الوسط */}
                <Box
                  sx={{ 
                    display: "flex", 
                    alignItems: "center", 
                    gap: { xs: 0.5, sm: 1 }, 
                    order: 2,
                    flex: 1,
                    justifyContent: "center",
                  }}
                >
                  <Typography 
                    sx={{ 
                      fontWeight: 600, 
                      fontSize: { xs: 13, sm: 15, md: 16 },
                      whiteSpace: "nowrap",
                    }}
                  >
                    {order.status}
                  </Typography>

                  {/* زر تعديل الحالة */}
                  {hasPermission("orders_other_edite_service") && (
                    <IconButton
                      size="small"
                      sx={{ 
                        ml: 0.5, 
                        color: "#FFC107",
                        p: { xs: 0.3, sm: 0.5 },
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditOrderId(order.id);
                        setEditStatusOpen(true);
                      }}
                    >
                      <EditIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
                    </IconButton>
                  )}
                  <Box
                    sx={{
                      width: { xs: 8, sm: 10 },
                      height: { xs: 8, sm: 10 },
                      borderRadius: "50%",
                      bgcolor:
                        order.status === "مكتمل"
                          ? "#4CAF50"
                          : order.status === "في التجهيز"
                          ? "#FFC107"
                          : order.status === "قيد التوصيل"
                          ? "#2196F3"
                          : "#fff",
                    }}
                  />
                </Box>
                {/* رقم الطلب - في النهاية */}
                <Box
                  sx={{
                    bgcolor: "#1ecb6b",
                    color: "#fff",
                    px: { xs: 1.5, sm: 2 },
                    py: { xs: 0.3, sm: 0.5 },
                    borderRadius: { xs: "10px", sm: "12px" },
                    fontWeight: 700,
                    fontSize: { xs: 15, sm: 17, md: 18 },
                    boxShadow: "0 2px 8px #0002",
                    display: "flex",
                    alignItems: "center",
                    minWidth: { xs: 50, sm: 60 },
                    justifyContent: "center",
                    order: 3,
                  }}
                >
                  {order.orderNumber}
                </Box>
              </Box>

              {/* Time */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: { xs: 1, sm: 2 },
                  pt: { xs: 0.3, sm: 0.5 },
                }}
              >
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: "#b0bec5",
                    fontSize: { xs: "10px", sm: "12px" },
                  }}
                >
                  {formatTime12(order.time)}
                </Typography>
                {/* تم إلغاء التايمر */}
              </Box>

              {/* Main Content */}
              <Box
                sx={{
                  px: { xs: 1, sm: 2 },
                  py: { xs: 1, sm: 1.5 },
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                  alignItems: "flex-end",
                }}
              >
                <Typography
                  sx={{ 
                    fontWeight: 700, 
                    fontSize: { xs: 15, sm: 17, md: 18 }, 
                    color: "#fff",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: "100%",
                  }}
                >
                  {order.customer}
                </Typography>
                <Typography 
                  sx={{ 
                    fontSize: { xs: 13, sm: 14, md: 15 }, 
                    color: "#fff",
                    direction: "ltr",
                  }}
                >
                  {order.phone}
                </Typography>
                <Typography 
                  sx={{ 
                    fontSize: { xs: 13, sm: 14, md: 15 }, 
                    color: "#b0bec5",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: "100%",
                  }}
                >
                  {order.address}
                </Typography>
                <Typography
                  sx={{ 
                    fontSize: { xs: 13, sm: 14, md: 15 }, 
                    color: "#90caf9", 
                    fontWeight: 500,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: "100%",
                  }}
                >
                  الدليفري: {order.pilotName}
                </Typography>
              </Box>

              {/* Footer: Total */}
              <Box
                sx={{
                  bgcolor: "#F8BBD0",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: { xs: 17, sm: 19, md: 20 },
                  px: { xs: 2, sm: 2.5 },
                  py: { xs: 0.5, sm: 0.7 },
                  borderRadius: { xs: "14px", sm: "16px" },
                  position: "absolute",
                  bottom: { xs: 8, sm: 12 },
                  right: { xs: 8, sm: 12 },
                  boxShadow: "0 2px 8px #0002",
                  minWidth: { xs: 60, sm: 70 },
                  textAlign: "center",
                  letterSpacing: 1.2,
                  border: "2px solid #fff2",
                }}
              >
                ${order.total}
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
      {/* Dialog TableModalDialog */}
      <DeliveryModalContent
        open={isAddItemsOpen}
        onClose={() => setIsAddItemsOpen(false)}
        type="addItem"
        orderId={selectedOrderId}
        tableId={null}
        rootId={localStorage.getItem("branch_id")}
        onSuccess={() => {
          setIsAddItemsOpen(false);
          loadOrders();
        }}
      />
      {/* مودال تغيير الحالة */}
      {editStatusOpen && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            bgcolor: "rgba(0,0,0,0.25)",
            zIndex: 1500,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              bgcolor: mode === "dark" ? "#232334" : "#fff",
              borderRadius: 3,
              boxShadow: 8,
              p: 3,
              minWidth: 300,
              maxWidth: 350,
              textAlign: "center",
              position: "relative",
              color: mode === "dark" ? "#fff" : "#232334",
            }}
          >
            <Typography
              variant="h6"
              sx={{ mb: 2, color: mode === "dark" ? "#fff" : "#232334" }}
            >
              تغيير حالة الطلب
            </Typography>
            {/* عرض اسم العميل ورقم الهاتف بجوار تغيير الحالة */}
            {(() => {
              const order = orders.find((o) => o.id === editOrderId);
              if (!order) return null;
              return (
                <Box
                  sx={{
                    mb: 2,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: "bold",
                      color: mode === "dark" ? "#fff" : "#232334",
                    }}
                  >
                    العميل: {order.customer}
                  </Typography>
                  <Typography
                    sx={{ color: mode === "dark" ? "#fff" : "#232334" }}
                  >
                    الهاتف: {order.phone}
                  </Typography>
                </Box>
              );
            })()}
            <TextField
              select
              label="اختر الحالة الجديدة"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              fullWidth
              sx={{
                mb: 2,
                bgcolor: mode === "dark" ? "#232334" : "#fff",
                color: mode === "dark" ? "#fff" : "#232334",
                "& .MuiInputBase-input": {
                  color: mode === "dark" ? "#fff" : "#232334",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: mode === "dark" ? "#888" : "#ccc",
                },
              }}
              SelectProps={{
                MenuProps: {
                  disablePortal: true,
                  PaperProps: {
                    sx: {
                      zIndex: 2000,
                      bgcolor: mode === "dark" ? "#232334" : "#fff",
                      color: mode === "dark" ? "#fff" : "#232334",
                      "& .MuiMenuItem-root": {
                        "&:hover": {
                          bgcolor:
                            mode === "dark" ? "#333" : "rgba(0,0,0,0.04)",
                        },
                      },
                    },
                  },
                },
              }}
            >
              {statusOptions.map((opt) => (
                <MenuItem
                  key={opt.value}
                  value={opt.value}
                  sx={{
                    bgcolor: mode === "dark" ? "#232334" : "#fff",
                    color: mode === "dark" ? "#fff" : "#232334",
                  }}
                >
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
            <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleChangeOrderStatus}
                disabled={!selectedStatus}
              >
                حفظ
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => {
                  setEditStatusOpen(false);
                  setEditOrderId(null);
                  setSelectedStatus("");
                }}
              >
                إلغاء
              </Button>
            </Box>
          </Box>
        </Box>
      )}
      {/* NEW: Dialog عرض أصناف الأوردر */}
      <OrderItemsDialog
        open={orderItemsDialogOpen}
        onClose={handleCloseOrderItems}
        orderId={selectedOrderIdForItems}
      />
    </Box>
  );
};

export default DeliverySection;
